<template>
  <div
    class="accordion__item"
    :class="[
      $blockClass(blockClass, 'accordion-item')
    ]"
  >
    <div
      ref="container"
      :style="animationVars"
      class="accordion__item-wrapper"
    >
      <button
        ref="heading"
        :class="[
          $blockClass(blockClass, 'accordion-heading'),
          (small ? 'subtitle-1' : 'h6')
        ]"
        class="accordion__heading"
        @click="handleAccordionClick(item)"
      >
        <span
          v-if="item.icon"
          :class="$blockClass(blockClass, 'accordion-icon')"
          class="accordion__icon"
        >
          <img
            :alt="`${item.title} Icon`"
            :src="item.icon"
          >
        </span>
        <span
          v-if="item.title"
          class="accordion__heading-title"
          v-text="item.title"
        />
        <span
          :class="$blockClass(blockClass, 'accordion-control-icon')"
          class="accordion__control-icon"
          v-html="accordionIcon"
        />
      </button>

      <div
        ref="content"
        :class="[
          $blockClass(blockClass, 'accordion-content'),
          {
            'accordion__content--placeholder': !ready,
            'accordion__content--checklist': item.enable_checklist
          },
        ]"
        class="accordion__content"
      >
        <div
          v-if="item.description"
          class="body-1"
          v-html="description"
        />

        <btn
          v-if="item.cta_copy && item.cta_url"
          :block-class="blockClass"
          :icon="false"
          :label="item.cta_copy"
          :url="item.cta_url"
          class="button--text"
        />

        <slot name="customContent" />

        <template v-if="item.description_list_1 || item.description_list_2">
          <div class="caption accordion__description-list-container">
            <template v-if="!item.enable_checklist">
              <div
                class="accordion__description-list"
                v-html="getDescriptionList(item.description_list_1)"
              />
              <div
                class="accordion__description-list"
                v-html="getDescriptionList(item.description_list_2)"
              />
            </template>
            <template v-else>
              <ul
                v-for="(list, listIndex) in descriptionLists"
                :key="`list-${listIndex}`"
                class="accordion__description-list"
              >
                <li
                  v-for="(listItem, listItemIndex) in list"
                  :key="`listItem-${listItemIndex}`"
                  class="accordion__list-item-container"
                >
                  <label class="accordion__list-item caption">
                    <button
                      :aria-label="$string('checklist.accessibility.check')"
                      :class="{ [$class('active')]: getItemIndex(listIndex, listItemIndex) > -1 }"
                      class="checkbox"
                      @click="toggleItem(listIndex, listItemIndex)"
                    >
                      <span class="checkbox__label" />
                    </button>
                    <span
                      ref="listItems"
                      v-html="listItem"
                    />
                  </label>
                </li>
              </ul>
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: accordion-item (accordion-item)
 * -----------------------------------------------------------------------------
 * A sub-component of an accordion.
 * Contains collapsible content for one accordion item.
 *
 * @param {String} blockClass - The block class to apply to all elements.
 * @param {Boolean} cleanRichtext - Strip richtext elements from description.
 * @param {Object} item - The accordion item.
 * @param {Boolean} item.active - The accordion item's open or closed state.
 * @param {String} item.cta_copy - The cta (text link) copy.
 * @param {String} item.cta_url - The text link URL.
 * @param {String} item.description - The text content inside accordion.
 * @param {String} item.description_list_1 - Richtext column 1
 * @param {String} item.description_list_2 - Richtext column 2
 * @param {Boolean} item.enable_checklist - Enable checklist features
 * @param {String} item.icon - The icon in the accordion header button.
 * @param {String} item.title - The text in the accordion header button.
 * @param {Boolean} small - Use small styling for accordion.
 */

import { mapState } from 'vuex'
import { nextTick } from 'vue'


export default {
  name: 'AccordionItem',

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    cleanRichtext: {
      type: Boolean,
      default: true,
    },
    item: {
      type: Object,
      default: () => ({}),
    },
    small: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['accordionToggle'],

  data() {
    return {
      animating: false,
      animationClasses: {
        open: 'accordion--open',
        close: 'accordion--close',
      },
      checklist: [],
      descriptionLists: [],
      heights: {
        initial: 0,
        final: 0,
        offset: 0,
      },
      initial: true,
      ready: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Compute the accordion's control icon based on its state.
     * @returns {*}
     */
    accordionIcon() {
      return this.item.active ?
        this.$icon('enclosed.minus') :
        this.$icon('enclosed.plus')
    },

    /**
     * Compute CSS variables to be used in the "expand" animation.
     * @returns {string}
     */
    animationVars() {
      const initial = this.initial && this.item.active ?
        this.heights.final :
        this.heights.initial
      return `
        --initial-height: ${initial}px;
        --final-height: ${this.heights.final}px;
        --back-offset: ${this.heights.offset}px;
      `.trim()
    },

    checklistKey() {
      return `dormify_checklist_${this.item.id}`
    },

    description() {
      const item = this.item
      if(item.description.includes('<ul>')) return item.description.replaceAll('<ul>', '<ul style="list-style: unset; padding-left: 2rem">');
      return this.cleanRichtext ? this.$cleanRTE(item.description) : item.description.split(/\r?\n|\r|\n/g).join("<br>")
    },
  },

  watch: {
    descriptionLists() {
      nextTick(() => {
        const { listItems } = this.$refs
        if (!listItems) {
          return
        }
        listItems.forEach(item => {
          const collectionAnchors = item.querySelectorAll('a[href*="/collections/"]')
          collectionAnchors.forEach(anchorEl => {
            anchorEl.addEventListener('click', event => {
              if (anchorEl.target === '_blank') {
                const anchor = event.target.closest('a')
                if (anchor) {
                  this.$gtmTrack('ChecklistClick', 'route_to_plp', {
                    url: anchor.href,
                  })
                }
              }
              else {
              event.preventDefault()
              const anchor = event.target.closest('a')
              if (anchor) {
                this.$gtmTrack('ChecklistClick', 'route_to_plp', {
                  url: anchor.href,
                })
                window.location = anchor.href
              }
              }
            })
          })
        })
      })
    },

    screenWidth() {
      this.setInitialHeights()
    },
  },

  mounted() {
    if (this.item.enable_checklist) {
      this.descriptionLists = [
        this.splitLines(this.item.description_list_1),
        this.splitLines(this.item.description_list_2),
      ]
    }
    this.loadChecklist()
    this.setInitialHeights()
    this.setEventListeners()
  },

  methods: {

    /**
     * Check if item is already in checklist
     * @param {Number} column - The column the item belongs to (0 or 1)
     * @param {Number} itemIndex - The item's index in the column
     * @returns {number}
     */
    getItemIndex(column, itemIndex) {
      return this.checklist.indexOf(`${column}-${itemIndex}`)
    },

    /**
     * Strip unnecessary tags in rich text and return a list of items. The
     * first dash in the line of text will be transformed into a bullet point.
     * @param descriptionList - The rich text to strip and format.
     * @returns {string}
     */
    getDescriptionList(descriptionList) {
      const description = this.$cleanRTE(descriptionList, { wrapLineBreaks: true })
      return description.replaceAll('<span>-', '<span>&#8226;')
    },

    /**
     * Emit an event when an accordion is clicked.
     * @param {Object} item - The accordion that was clicked.
     */
    handleAccordionClick(item) {
      if (this.animating) {
        return
      }
      const { classList } = this.$refs.container
      const { open, close } = this.animationClasses
      this.initial = false
      item.active = !item.active
      this.$emit('accordionToggle', item)
      classList.remove(...Object.values(this.animationClasses))
      requestAnimationFrame(() => {
        classList.add(item.active ? open : close)
      })
    },

    /**
     * Load checklist from local storage.
     */
    loadChecklist() {
      try {
        this.checklist = JSON.parse(localStorage.getItem(this.checklistKey)) || []
      } catch (e) {
        this.checklist = []
      }
    },

    /**
     * Save checklist to local storage.
     */
    saveChecklist() {
      localStorage.setItem(this.checklistKey, JSON.stringify(this.checklist))
    },

    /**
     * Event listeners to lock accordion clicks during animations.
     */
    setEventListeners() {
      const { container } = this.$refs
      container.addEventListener('animationstart', () =>
        this.animating = true,
      )
      container.addEventListener('animationend', () => {
        this.animating = false
      })
    },

    /**
     * Set the accordion item height variables.
     */
    setInitialHeights() {
      const { heading, content } = this.$refs
      this.heights.initial = heading.clientHeight
      this.heights.offset = this.heights.initial * .10
      this.ready = true
      nextTick(() => {
        this.heights.final = content.clientHeight + this.heights.initial
      })
    },

    /**
     * Get array of text lines from richtext.
     * @param {String} rte - Richtext content from theme settings.
     * @returns {*}
     */
    splitLines(rte) {
      return rte.split('</p>').filter(Boolean).map(
        line => line.replace('<p>-', '').trim(),
      )
    },

    /**
     * Remove or add item to checklist
     * @param {Number} column - The column the item belongs to (0 or 1)
     * @param {Number} itemIndex - The item's index in the column
     */
    toggleItem(column, itemIndex) {
      const index = this.getItemIndex(column, itemIndex)
      if (index > -1) {
        this.checklist.splice(index, 1)
      } else {
        this.checklist.push(`${column}-${itemIndex}`)
      }
      this.saveChecklist()
    },
  },
}
</script>
